/**
 * トップ画面（料金・お知らせ）
 * NURO 光マイページのメインダッシュボード。
 * プラン状態（開通済/未開通）に応じて表示セクションを切り替える:
 * - 開通済み: 利用料金 + 開通工事進捗 + お知らせ + 配送情報
 * - 未開通: 開通工事進捗 + 利用料金 + お知らせ + 配送情報
 * - NURO以外: 利用料金 + お知らせ + 配送情報
 * ログイン直後にポップアップメッセージがあれば表示する。
 */
import type { NextPage } from 'next';
import ScrollLinkList from '@/components/molecules/ScrollLinkList';
import Heading from '@/components/atoms/Heading';
import BreadcrumbLists from '@/components/templates/BreadcrumbLists';
import { userState } from '@/states/selectors/userState';
import { useRecoilValue } from 'recoil';
import DeliveryInfo from '@/components/organisms/DeliveryInfo';
import NuroAppBanner from '@/components/organisms/NuroAppBanner';
import ConstructionProgress from '@/components/organisms/ConstructionProgress';
import ServiceOftenUseInfo from '@/components/organisms/ServiceOftenUseInfo';
import useCallAPIGetOptionInfo from '@/hooks/api/useCallAPIGetOptionInfo';
import { useEffect, useState } from 'react';
import { AnalyticsData } from '@/components/templates/AnalyticsData';
import LoginMessagePopup from '@/components/organisms/LoginMessagePopup';
import { LoginMessageState } from '@/states/loginMessageState';
import { Popup } from '@/types/api/mypage/loginMessage';
import { clearLoginSuccessFlag, isLoginSuccessful } from '@/utils/cognito';
import BillingInfo from '../../components/organisms/BillingInfo';

import News from '../../components/organisms/News';

const Top: NextPage = () => {
  const user = useRecoilValue(userState);
  const { execute: callAPIGetOptionInfo } = useCallAPIGetOptionInfo();
  const loginMessage = useRecoilValue(LoginMessageState);
  const [showPopup, setShowPopup] = useState(false);
  const [popups, setPopups] = useState<Popup[]>([]);
  /** 現在表示中のポップアップのインデックス */
  const [currentPopupIndex, setCurrentPopupIndex] = useState(0);

  // マウント時にオプション情報を取得し、ログイン直後ならポップアップ表示を開始する
  useEffect((): void => {
    callAPIGetOptionInfo();
    if (isLoginSuccessful() && loginMessage.popups) {
      setShowPopup(true);
      setPopups(loginMessage.popups);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * ポップアップを閉じる処理
   * 複数のポップアップがある場合は次のポップアップを表示し、
   * 全て表示完了したらポップアップを非表示にしログイン成功フラグをクリアする。
   */
  const handleClosePopup = () => {
    if (currentPopupIndex < popups.length - 1) {
      setCurrentPopupIndex(currentPopupIndex + 1);
    } else {
      setPopups([]);
      setCurrentPopupIndex(0);
      setShowPopup(false);
      clearLoginSuccessFlag();
    }
  };

  if (!user.isNuro) {
    return (
      <>
        {showPopup && popups.length > 0 && (
          <LoginMessagePopup
            title={popups[currentPopupIndex].title}
            text={popups[currentPopupIndex].text}
            link={popups[currentPopupIndex].link}
            onClose={handleClosePopup}
          />
        )}
        <AnalyticsData />
        <main className='l-main'>
          <Heading as='h1'>料金・お知らせ</Heading>
          <ScrollLinkList
            items={[
              { anchor: 'billing-info', title: 'ご利用料金' },
              { anchor: 'notification', title: 'お知らせ' },
              { anchor: 'delivery-info', title: '配送情報' },
            ]}
          />
          <BillingInfo
            anchor='billing-info'
            plan={user.planName}
            customerNumber={user.ispCnNo}
            isOpen={user.isOpen}
          />
          <News anchor='notification' />
          <NuroAppBanner />
          <DeliveryInfo anchor='delivery-info' />
          <ServiceOftenUseInfo anchor='service-often-use-info' />
          <BreadcrumbLists />
        </main>
      </>
    );
  }

  if (user.isOpen) {
    return (
      <>
        {showPopup && popups.length > 0 && (
          <LoginMessagePopup
            title={popups[currentPopupIndex].title}
            text={popups[currentPopupIndex].text}
            link={popups[currentPopupIndex].link}
            onClose={handleClosePopup}
          />
        )}
        <AnalyticsData />
        <main className='l-main'>
          <Heading as='h1'>料金・お知らせ</Heading>
          <ScrollLinkList
            items={[
              { anchor: 'billing-info', title: 'ご利用料金' },
              { anchor: 'constuction-progress', title: '開通工事の進捗' },
              { anchor: 'notification', title: 'お知らせ' },
              { anchor: 'delivery-info', title: '配送情報' },
            ]}
          />
          <BillingInfo
            anchor='billing-info'
            plan={user.planName}
            customerNumber={user.ispCnNo}
            isOpen={user.isOpen}
          />
          <ConstructionProgress anchor='constuction-progress' />
          <News anchor='notification' />
          <NuroAppBanner />
          <DeliveryInfo anchor='delivery-info' />
          <ServiceOftenUseInfo anchor='service-often-use-info' />
          <BreadcrumbLists />
        </main>
      </>
    );
  }

  return (
    <>
      {showPopup && popups.length > 0 && (
        <LoginMessagePopup
          title={popups[currentPopupIndex].title}
          text={popups[currentPopupIndex].text}
          link={popups[currentPopupIndex].link}
          onClose={handleClosePopup}
        />
      )}
      <AnalyticsData />
      <main className='l-main'>
        <Heading as='h1'>料金・お知らせ</Heading>
        <ScrollLinkList
          items={[
            { anchor: 'constuction-progress', title: '開通工事の進捗' },
            { anchor: 'billing-info', title: 'ご利用料金' },
            { anchor: 'notification', title: 'お知らせ' },
            { anchor: 'delivery-info', title: '配送情報' },
          ]}
        />
        <ConstructionProgress anchor='constuction-progress' />
        <BillingInfo
          anchor='billing-info'
          plan={user.planName}
          customerNumber={user.ispCnNo}
          isOpen={user.isOpen}
        />
        <News anchor='notification' />
        <NuroAppBanner />
        <DeliveryInfo anchor='delivery-info' />
        <ServiceOftenUseInfo anchor='service-often-use-info' />
        <BreadcrumbLists />
      </main>
    </>
  );
};

export default Top;
